import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Accordion,
  Banner,
  CloseButton,
  GridContent,
  GridLayout,
  Image,
  List,
  Page,
  Radio,
  Spacer,
  TextButton,
} from '@zillow/constellation';
import { Button, Card, Heading, Paragraph, Text } from '@zillow/constellation-10';

import { ZillowInsuranceServicesLogo } from '@zillow/zrs-comet';

import { RMWContext } from '../../../providers/RMWContext';

import { useSelector } from '../../../selectors/useSelector';

import numberUtils from '../../../utils/numberUtils';
import PropertiesActions from '../../../actions/PropertiesActions';
import SpinnerContainer from '../../SpinnerContainer';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import OptInCSEvents from '../../../model/ClickstreamEvent/RentalProtection/OptInCSEvents';
import ModalFooter from '../../Modal/ModalFooter';
import {
  StyledBannerImageDiv,
  StyledCoverageOptionsContainer,
  StyledImageGridContainer,
  StyledPageBreadcrumb,
  StyledPageContent,
  StyledTextButton,
} from './StyledComponents';
import { StyledHidden } from '../StyledComponents';

import { useCompleteLightweightOptIn } from '../../../hooks/queries/rentalProtection';
import RentalProtectionHowDoesItWorkModal from '../../Modal/RentalProtectionHowDoesItWorkModal';
import RentalProtectionWhatsCoveredModal from '../../Modal/RentalProtectionWhatsCoveredModal';
import RentalProtectionLearnMoreModal from '../../Modal/RentalProtectionLearnMoreModal';
import PropertyDamageIcon from '../../../images/rentalProtection/property-damage.svg';
import FolderIcon from '../../../images/rentalProtection/folder-with-paperclip.svg';
import HomeSparkleIcon from '../../../images/HomeSparkle.svg';
import { getIsLowFrictionEnabledFromProperty } from '../../../utils/rentalProtectionUtils';
import LeaseQualificationAccordion from '../components/LeaseQualificationAccordion';

import type { PremiumCoverage } from '../../../model/RentGuarantee';
import { updateLocation } from '../../../slices/app';

type Props = {
  listingAlias: string;
};

const CoverageLevelSelection = ({ listingAlias }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { paths } = useContext(RMWContext);

  const {
    currentProperty: {
      rentalProtection: {
        premiumCoverages,
        additionalDetails: {
          urls: { faq, coverageSummary: coverageSummaryUrl },
        },
      },
    },
    currentProperty,
  } = useSelector((state) => state.properties);

  const user = useSelector((state) => state.user);

  const { isLoading, data: propertyDetailsData } = useGetListingDetails(listingAlias);
  const { mutateAsync: completeLightweightOptIn } = useCompleteLightweightOptIn();
  const [hasApiError, setHasApiError] = useState<boolean>(false);
  const [selectedPremiumCoverageOption, setSelectedPremiumCoverageOption] = useState<PremiumCoverage>();
  const [showWhatsCoveredModal, setShowWhatsCoveredModal] = useState<boolean>(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState<boolean>(false);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState<boolean>(false);

  const isLowFrictionEnabled = useMemo(
    // @ts-expect-error
    () => getIsLowFrictionEnabledFromProperty(propertyDetailsData),
    [propertyDetailsData],
  );

  useEffect(() => {
    OptInCSEvents.COVERAGE_SELECTION.ViewCoverageSelectionPage(csEventParams);
  }, []);

  const getDashboardUrl = () => {
    return `${paths.propertiesPage}/${listingAlias}/dashboard/overview`;
  };

  const csEventParams = useMemo(() => ({ user, currentProperty }), [user, currentProperty]);

  const handleBreadcrumbClick = () => {
    history.push(getDashboardUrl());
  };

  const onHowItWorksClick = () => {
    OptInCSEvents.COVERAGE_SELECTION.ClickHowItWorks(csEventParams);
    setShowHowItWorksModal(true);
  };

  const onLearnMoreClick = () => {
    OptInCSEvents.COVERAGE_SELECTION.ClickLearnMore(csEventParams);
    setShowLearnMoreModal(true);
  };

  const onWhatsCoveredClick = () => {
    OptInCSEvents.COVERAGE_SELECTION.ClickWhatsCovered(csEventParams);
    setShowWhatsCoveredModal(true);
  };

  const handleSelectCoverageOption = () => {
    if (selectedPremiumCoverageOption) {
      Promise.resolve(
        dispatch(
          PropertiesActions.selectRentalProtectionCoverageLevelForListing({
            listingAlias,
            premiumCoverageId: selectedPremiumCoverageOption.premiumCoverageId,
          }),
        ),
      )
        .then(() => {
          return completeLightweightOptIn({ listingAlias });
        })
        .then(() => {
          dispatch(
            updateLocation(
              `${paths.rentGuaranteePage}/properties/${listingAlias}/managePolicies/create-new?showSelectedCoverageToast=true`,
            ),
          );
        })
        .catch(() => {
          setHasApiError(true);
        });
    }
  };

  const renderInfoSection = () => {
    return (
      <GridLayout marginX={{ default: 0, md_lte: 'sm' }}>
        <GridContent gridColumnEnd="span 12" paddingBottom={0} paddingX={0}>
          <Paragraph textStyle="body-bold" color="text.subtle">
            How does it work?
          </Paragraph>
        </GridContent>
        <StyledImageGridContainer gridColumnEnd="span 2" padding={0}>
          <Image src={PropertyDamageIcon} width="44" height="44" alt="broken property" />
        </StyledImageGridContainer>
        <GridContent gridColumnEnd="span 10" padding={0}>
          <Paragraph textStyle="body-bold">Protect against missed rent and damages</Paragraph>
          <Paragraph textStyle="body-sm" color="text.subtle">
            Gain peace of mind with coverage for things like pet damage, eviction costs, and more.
          </Paragraph>
          <Paragraph>
            <StyledTextButton onClick={onWhatsCoveredClick}>What&apos;s covered</StyledTextButton>
          </Paragraph>
        </GridContent>
        <StyledImageGridContainer gridColumnEnd="span 2" padding={0}>
          <Image src={HomeSparkleIcon} width="44" height="44" alt="home" />
        </StyledImageGridContainer>
        <GridContent gridColumnEnd="span 10" padding={0}>
          <Paragraph textStyle="body-bold">Use with existing landlord insurance</Paragraph>
          <Paragraph textStyle="body-sm" color="text.subtle">
            Rental Protection picks up where your standard landlord property insurance leaves off.
          </Paragraph>
          <Paragraph>
            <StyledTextButton onClick={onLearnMoreClick}>Learn More</StyledTextButton>
          </Paragraph>
        </GridContent>
        <GridContent gridColumnEnd="span 12" paddingBottom={0} paddingX={0}>
          <Paragraph textStyle="body-bold" color="text.subtle">
            What&apos;s next?
          </Paragraph>
        </GridContent>
        <StyledImageGridContainer gridColumnEnd="span 2" padding={0}>
          <Image src={FolderIcon} width="44" height="44" alt="folder" />
        </StyledImageGridContainer>
        <GridContent gridColumnEnd="span 10" padding={0}>
          {isLowFrictionEnabled ? (
            <div data-testid="WhatsNextLowFrictionDescription">
              <Paragraph textStyle="body-bold">Activate coverage before your lease starts</Paragraph>
              <Paragraph textStyle="body-sm" color="text.subtle">
                Share your lease details and pay for your policy to activate coverage.
              </Paragraph>
            </div>
          ) : (
            <div data-testid="WhatsNextZToolsDescription">
              <Paragraph textStyle="body-bold">Activate Zillow&apos;s free tools</Paragraph>
              <Paragraph textStyle="body-sm" color="text.subtle">
                Rental Protection requires use of Zillow applications, leases and payments.
              </Paragraph>
            </div>
          )}
          <Paragraph>
            <StyledTextButton onClick={onHowItWorksClick}>How it works</StyledTextButton>
          </Paragraph>
        </GridContent>
      </GridLayout>
    );
  };

  if (isLoading) {
    return <SpinnerContainer />;
  }

  if (hasApiError) {
    return (
      <Banner
        appearance="error"
        body="Oops, something went wrong. Please refresh the page and try again"
        onClose={() => window.location.reload()}
      />
    );
  }

  return (
    <Page backgroundColor="white" data-testid="CoverageLevelSelectionPage">
      <StyledPageBreadcrumb
        backButton={
          <TextButton fontType="bodyHeading" onClick={handleBreadcrumbClick} data-testid="breadcrumb-close-button">
            Back
          </TextButton>
        }
      />
      <StyledPageContent
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        marginTop={{ default: 'lg', md_lte: '0' }}
        marginBottom="lg"
      >
        <GridLayout>
          <GridContent gridColumnEnd={{ default: 'span 7', md_lte: 'span 12' }} padding={{ default: 'md', md_lte: 0 }}>
            <StyledHidden breakpoint="md_lte">
              <ZillowInsuranceServicesLogo />
              <Heading level={1} textStyle="heading-md" fontFamily="heading-alt">
                Rental Protection
              </Heading>
              <Spacer marginTop="md">
                <Heading level={2} textStyle="heading-xs" fontFamily="heading-alt">
                  Insurance for landlords
                </Heading>
                <Paragraph>Choose coverage that works with or replaces your security deposit.</Paragraph>
              </Spacer>
            </StyledHidden>
            <StyledHidden breakpoint="lg_gte">
              <StyledBannerImageDiv>
                <ZillowInsuranceServicesLogo marginX="sm" />
                <Heading level={1} textStyle="heading-md" fontFamily="heading-alt" css={{ marginInline: 'sm' }}>
                  Rental Protection
                </Heading>
                <Spacer marginY="xs">
                  <Heading level={2} textStyle="heading-xs" fontFamily="heading-alt" css={{ marginInline: 'sm' }}>
                    Insurance for landlords
                  </Heading>
                </Spacer>
                <Paragraph css={{ marginInline: '400' }}>
                  Choose coverage that works with or replaces your security deposit.
                </Paragraph>
              </StyledBannerImageDiv>
              {renderInfoSection()}
            </StyledHidden>
            <Spacer marginTop="md" marginX={{ default: 0, md_lte: 'sm' }}>
              <Paragraph css={{ textStyle: 'heading-xs' }}>Select your coverage amount:</Paragraph>
              <Paragraph>You can adjust this before you purchase a policy.</Paragraph>
            </Spacer>
            {premiumCoverages.map((premiumCoverage: PremiumCoverage, index: number) => {
              return (
                <Spacer marginTop="sm" marginX={{ default: 0, md_lte: 'sm' }}>
                  <StyledCoverageOptionsContainer onClick={() => setSelectedPremiumCoverageOption(premiumCoverage)}>
                    <GridContent gridColumnEnd={{ default: 'span 2' }}>
                      <Radio
                        name={'radio-coverage-option-' + premiumCoverage.coverage}
                        onClick={() => setSelectedPremiumCoverageOption(premiumCoverage)}
                        checked={selectedPremiumCoverageOption?.premiumCoverageId === premiumCoverage.premiumCoverageId}
                      />
                    </GridContent>
                    <GridContent gridColumnEnd={{ default: 'span 5', md_lte: 'span 6' }} paddingX={{ md_lte: 0 }}>
                      <Paragraph textStyle="body-sm">Coverage (per lease)</Paragraph>
                      <Paragraph css={{ textStyle: 'heading-sm' }}>
                        {numberUtils.asCurrencyFromCents(premiumCoverage.coverage)}
                      </Paragraph>
                    </GridContent>
                    <GridContent gridColumnEnd={{ default: 'span 5', md_lte: 'span 4' }} paddingX={{ md_lte: 0 }}>
                      <Paragraph textStyle="body-sm">Monthly cost^</Paragraph>
                      <Paragraph css={{ textStyle: 'heading-sm' }}>
                        {numberUtils.asCurrencyFromCents(premiumCoverage.premium)}
                      </Paragraph>
                    </GridContent>
                  </StyledCoverageOptionsContainer>
                </Spacer>
              );
            })}
            <Spacer marginTop="md" marginX={{ default: 0, md_lte: 'sm' }}>
              <StyledHidden breakpoint="lg_gte">
                <Button
                  tone="brand"
                  emphasis="filled"
                  onClick={handleSelectCoverageOption}
                  fluid
                  disabled={!selectedPremiumCoverageOption}
                >
                  Go to dashboard
                </Button>
              </StyledHidden>
              <StyledHidden breakpoint="md_lte">
                <Button
                  tone="brand"
                  emphasis="filled"
                  onClick={handleSelectCoverageOption}
                  disabled={!selectedPremiumCoverageOption}
                >
                  Go to dashboard
                </Button>
              </StyledHidden>
              <Paragraph textStyle="body-xs" color="text.subtle" css={{ marginTop: '400' }}>
                No commitment to purchase a policy today.
              </Paragraph>
            </Spacer>
            <Spacer marginTop="md" marginX={{ default: 0, md_lte: 'sm' }}>
              <Paragraph textStyle="body-xs" color="text.subtle" css={{ marginTop: '800' }}>
                ^plus applicable surplus lines taxes and fees
              </Paragraph>
            </Spacer>
          </GridContent>
          <StyledHidden breakpoint="md_lte">
            <GridContent gridColumnEnd={{ default: 'span 5', md_lte: 'span 12' }}>
              <StyledBannerImageDiv>
                <Card
                  tone="neutral"
                  css={{ marginTop: '1200', position: 'absolute', maxWidth: '450px', marginLeft: '-25px' }}
                >
                  {renderInfoSection()}
                </Card>
              </StyledBannerImageDiv>
            </GridContent>
          </StyledHidden>
          <GridContent
            gridColumnEnd={{ default: 'span 7', md_lte: 'span 12' }}
            paddingTop={0}
            paddingX={{ default: 'md', md_lte: 0 }}
          >
            <Spacer marginX={{ default: 0, md_lte: 'sm' }}>
              <Heading level={4} fontFamily="heading-alt" css={{ marginBottom: '600' }}>
                Learn more
              </Heading>
              <Spacer marginBottom="md">
                <Accordion>
                  <Accordion.Item>
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        How do I choose my coverage amount?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <Paragraph css={{ marginBottom: '400' }}>
                        Rental Protection is available in two coverage levels: (1) $5,000 coverage limit for a $39/month
                        premium or (2) $10,000 coverage limit for a $59/month premium.
                      </Paragraph>
                      <Paragraph css={{ marginBottom: '400' }}>
                        Both options offer the same type of coverage, the only difference is in the coverage limit and
                        monthly cost.
                      </Paragraph>
                      <Paragraph>
                        When considering which coverage limit to select, you may want to consider factors such as:
                      </Paragraph>
                      <List marginBottom="sm">
                        <List.Item paddingY={0}>
                          <Text asChild>
                            <i>Monthly rent:</i>
                          </Text>{' '}
                          Consider your rent amount and how many months of rent you want to account for. You may want to
                          consider a higher coverage limit if you have higher monthly rent or want to be prepared for
                          several months of missed payments.
                        </List.Item>
                        <List.Item paddingY={0}>
                          <Text asChild>
                            <i>Pets:</i>
                          </Text>{' '}
                          Consider potential sources of property damage your tenant may cause. For example, if your
                          tenant is moving in with pets, you may want to consider a higher coverage limit.
                        </List.Item>
                        <List.Item paddingY={0}>
                          <Text asChild>
                            <i>Security deposit:</i>
                          </Text>{' '}
                          If you&apos;re waiving or reducing your security deposit, you may want to make sure your
                          coverage limit equals at least your typical security deposit amount. If you&apos;re collecting
                          your typical security deposit amount, a lower coverage limit may be appropriate.
                        </List.Item>
                      </List>
                      <Paragraph>
                        You can change your coverage level at any time up to coverage request signing. Once you sign
                        your coverage request, you will no longer be able to change your coverage level.
                      </Paragraph>
                    </Accordion.Panel>
                  </Accordion.Item>
                  {isLowFrictionEnabled ? (
                    <LeaseQualificationAccordion />
                  ) : (
                    <Accordion.Item data-testid="RentalProtectionZToolsAccordion">
                      <Accordion.Header>
                        <Heading level={5} css={{ textStyle: 'body' }}>
                          Why does Rental Protection require use of Zillow&apos;s tools?
                        </Heading>
                      </Accordion.Header>
                      <Accordion.Panel>
                        <Paragraph css={{ marginBottom: '400' }}>
                          Zillow Insurance Services uses information it receives from Zillow Rental Manager to prepare
                          an insurance request on your behalf for Rental Protection coverage.
                        </Paragraph>
                        <Paragraph css={{ marginBottom: '400' }}>
                          Zillow Insurance Services also sends rental payment information to LeaseLock that can be used
                          to verify a claim for renter default.
                        </Paragraph>
                        <Paragraph>
                          Here&apos;s how the Zillow Rental Manager tools help with Rental Protection:
                        </Paragraph>
                        <List>
                          <List.Item paddingY={0}>
                            <Text textStyle="body-bold">Applications.</Text> Applicants can upload income documents to
                            help you verify tenants have the minimum income-to-rent ratio required for Rental
                            Protection.
                          </List.Item>
                          <List.Item paddingY={0}>
                            <Text textStyle="body-bold">Leases.</Text> Zillow Insurance Services can quickly send your
                            lease terms to LeaseLock.
                          </List.Item>
                          <List.Item paddingY={0}>
                            <Text textStyle="body-bold">Payments.</Text> The record of payment transactions between you
                            and the renter helps track if rent payments are missed, so Zillow Insurance Services can
                            provide that information to LeaseLock for claims processing.
                          </List.Item>
                        </List>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}
                  <Accordion.Item>
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        How do I submit a claim?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <Paragraph css={{ marginBottom: '400' }}>
                        You can start a claim from your Rental Protection policy details page when logged into your
                        Zillow Rental Manager account. You&apos;ll be redirected to LeaseLock Insurance Services, who
                        will handle your claim directly.
                      </Paragraph>

                      <Paragraph>
                        We&apos;ll provide LeaseLock with some of the required background information, such as the
                        renter&apos;s income docs, the lease they signed, and their payment history. LeaseLock will
                        provide you with instructions about the additional information they need to process your claim.
                      </Paragraph>
                    </Accordion.Panel>
                  </Accordion.Item>
                  <Accordion.Item>
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        Will my premium increase if I submit a claim?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <Paragraph css={{ marginBottom: '400' }}>
                        Your monthly premium will stay the same for the duration of the coverage period.
                      </Paragraph>

                      <Paragraph>
                        You can only submit one claim per coverage period - after a claim is submitted, you will no
                        longer pay premiums or have coverage.
                      </Paragraph>
                    </Accordion.Panel>
                  </Accordion.Item>
                  <Accordion.Item>
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        Can I cancel Rental Protection at any time?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <Paragraph css={{ marginBottom: '400' }}>
                        Yes, you can cancel coverage at any time. If you cancel Rental Protection, the coverage period
                        will continue until the end of the month for any month in which the premium has been paid. Any
                        premium paid during the coverage period is fully earned and non-refundable. Premium may be
                        refundable if it is paid for any month after cancellation.
                      </Paragraph>

                      <Paragraph>After the coverage period ends, you must file a claim within 120 days.</Paragraph>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Spacer>
              <Paragraph>
                For more information, visit our{' '}
                <TextButton as="a" target="_blank" rel="noopener noreferrer" href={faq}>
                  FAQ page
                </TextButton>{' '}
                or view a{' '}
                <TextButton as="a" target="_blank" rel="noopener noreferrer" href={coverageSummaryUrl}>
                  sample coverage summary.
                </TextButton>
              </Paragraph>
            </Spacer>
          </GridContent>
        </GridLayout>
      </StyledPageContent>
      {showHowItWorksModal && (
        <RentalProtectionHowDoesItWorkModal
          onClose={() => setShowHowItWorksModal(false)}
          footer={<ModalFooter submitText="Ok" onSubmit={() => setShowHowItWorksModal(false)} />}
          isLowFrictionEnabled={isLowFrictionEnabled}
        />
      )}
      {showWhatsCoveredModal && (
        <RentalProtectionWhatsCoveredModal
          onClose={() => setShowWhatsCoveredModal(false)}
          onSubmit={() => setShowWhatsCoveredModal(false)}
          // @ts-expect-error
          shouldCloseOnOutsideClick={true}
          renderCloseButton={<CloseButton />}
          coverageSummaryUrl={coverageSummaryUrl}
        />
      )}
      {showLearnMoreModal && (
        <RentalProtectionLearnMoreModal
          onClose={() => setShowLearnMoreModal(false)}
          onSubmit={() => setShowLearnMoreModal(false)}
          // @ts-expect-error
          shouldCloseOnOutsideClick={true}
          renderCloseButton={<CloseButton />}
        />
      )}
    </Page>
  );
};

export default CoverageLevelSelection;
